import { useCallback } from 'react';
import { useMutation } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { useParams } from 'react-router-dom';
import { BUDGET_CALCULATION_FRAGMENT, BUDGET_FORM_DATA_FRAGMENT } from 'graphql/fragments/budget';
import { useIntl } from 'react-intl';
import { compact } from 'lodash';
import { FINANCING_QUERY } from '../financing/useFinancing';

const BUDGET_CALCULATOR_MUTATION = gql`
  mutation budgetCalculator($projectId: ID, $formData: BudgetCalculatorInput!, $locale: String, $step: Int) {
    budgetCalculator(projectId: $projectId, formData: $formData, step: $step) {
      id
      negativeMonthlyRateLoan
      budgetEstimation {
        _id
        loan_request
        pdf(locale: $locale)
        ...budgetCalculation
        ...budgetFormData
      }
    }
  }
  ${BUDGET_CALCULATION_FRAGMENT}
  ${BUDGET_FORM_DATA_FRAGMENT}
`;

const useBudgetCalculator = () => {
  const [budgetCalculatorMutation] = useMutation(BUDGET_CALCULATOR_MUTATION);
  const { id: projectId } = useParams();
  const { locale } = useIntl();

  const budgetCalculator = useCallback(
    async (formData, step) => {
      try {
        const refetchQueries = [projectId ? { query: FINANCING_QUERY, variables: { id: projectId } } : null];

        const {
          data: { budgetCalculator: result },
        } = await budgetCalculatorMutation({
          variables: { projectId, formData, step, locale, ...(projectId ? { projectId } : {}) },
          refetchQueries: compact(refetchQueries),
          awaitRefetchQueries: true,
        });

        return result;
      } catch (error) {
        const e = error.graphQLErrors ? new Error(error.graphQLErrors.map((err) => err.message)) : error;
        e.raw = error || '';
        throw e;
      }
    },
    [budgetCalculatorMutation, projectId, locale],
  );
  return budgetCalculator;
};

export default useBudgetCalculator;
